import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, TableShell, EmptyState, Modal, Field, Input, Textarea, SelectBox, Button, StatusBadge,
} from "@/components/ui-kit";
import { IconBtn } from "@/pages/Clients";
import { Plus, UserPlus, Pencil, Trash2, ArrowRightCircle } from "lucide-react";

const EMPTY = {
  name: "", phone: "", email: "", address: "", city: "", state: "", zip: "",
  preferred_contact: "Phone", source: "Website", service_interest: "", status: "New", notes: "",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const { t } = useI18n();

  const load = () => api.get("/leads").then(({ data }) => setLeads(data));
  useEffect(() => {
    load();
    api.get("/services").then(({ data }) => setServices(data.map((s) => s.name)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (l) => { setForm(l); setEditing(l.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error(t("Lead name is required"));
    try {
      if (editing) await api.put(`/leads/${editing}`, form);
      else await api.post("/leads", form);
      toast.success(editing ? t("Lead updated") : t("Lead added"));
      setOpen(false); load();
    } catch { toast.error("Could not save lead"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`); toast.success(t("Lead deleted")); load();
  };

  const convert = async (lead) => {
    // Create an estimate pre-filled from the lead
    try {
      const payload = {
        lead_id: lead.id,
        customer_name: lead.name, customer_email: lead.email, customer_phone: lead.phone,
        address: lead.address, city: lead.city, state: lead.state, zip: lead.zip,
        line_items: lead.service_interest ? [{ service: lead.service_interest, description: "", qty: 1, price: 0 }] : [],
        status: "Draft",
      };
      const { data } = await api.post("/estimates", payload);
      await api.put(`/leads/${lead.id}`, { status: "Qualified" });
      toast.success(`Estimate ${data.estimate_number} created from lead`);
      load();
    } catch { toast.error("Could not convert lead"); }
  };

  return (
    <div data-testid="leads-page">
      <PageHeader title="Call Center" subtitle="Track inbound leads & calls and convert them into estimates."
        action={<PrimaryButton onClick={openNew} data-testid="add-lead-button"><Plus size={16} /> {t("Add Lead")}</PrimaryButton>} />

      {leads.length === 0 ? (
        <EmptyState icon={UserPlus} title="No leads yet" subtitle="Capture new prospects here. Convert a qualified lead into an estimate in one click."
          action={<PrimaryButton onClick={openNew}><Plus size={16} /> {t("Add Lead")}</PrimaryButton>} />
      ) : (
        <TableShell headers={["Name", "Contact", "Interest", "Source", "Status", "Actions"]} testid="leads-table">
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd]">
              <td className="py-3.5 px-4 font-medium text-[#121212]">{l.name}</td>
              <td className="py-3.5 px-4 text-sm"><div>{l.phone}</div><div className="text-xs text-[#52525B]">{l.email}</div></td>
              <td className="py-3.5 px-4 text-sm text-[#52525B]">{l.service_interest || "—"}</td>
              <td className="py-3.5 px-4 text-sm text-[#52525B]">{l.source}</td>
              <td className="py-3.5 px-4"><StatusBadge status={l.status} /></td>
              <td className="py-3.5 px-4">
                <div className="flex items-center gap-1">
                  {l.status !== "Converted" && (
                    <IconBtn onClick={() => convert(l)} title="Convert to Estimate" testid={`convert-lead-${l.id}`}><ArrowRightCircle size={16} /></IconBtn>
                  )}
                  <IconBtn onClick={() => openEdit(l)} title="Edit"><Pencil size={16} /></IconBtn>
                  <IconBtn onClick={() => remove(l.id)} title="Delete" danger><Trash2 size={16} /></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Lead" : "Add Lead"} size="xl"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-lead-button">{t("Save Lead")}</PrimaryButton></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="lead-name-input" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Preferred Contact"><SelectBox value={form.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)} options={["Phone", "Email", "Text"]} /></Field>
          <Field label="Service Interest"><SelectBox value={form.service_interest} onChange={(e) => set("service_interest", e.target.value)} options={services} placeholder="Select service" /></Field>
          <Field label="Source"><SelectBox value={form.source} onChange={(e) => set("source", e.target.value)} options={["Website", "Referral", "Google", "Social Media", "Phone Call", "Walk-in"]} /></Field>
          <Field label="Status"><SelectBox value={form.status} onChange={(e) => set("status", e.target.value)} options={["New", "Contacted", "Qualified", "Converted"]} /></Field>
          <Field label="Address" className="sm:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="ZIP"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} /></Field>
          </div>
          <Field label="Notes" className="sm:col-span-2"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
      </Modal>
    </div>
  );
}
